// packages/web/src/components/Avatar.tsx — WS5: the reusable PERSON AVATAR
// treatment (and the general image-slot pattern for people). Wikidata gives a
// person metadata but never a portrait, so the avatar is user-supplied: render
// the uploaded image (through the same object-URL MediaImage every stored blob
// uses) when one exists, else an INITIALS placeholder on a deterministic tinted
// circle. Shown wherever a person appears (worked-with, casting director, the
// person record editor).
//
//   • Avatar          — the presentational circle (image OR initials), given a
//                        media id + name. Used where the caller already knows
//                        the avatar id.
//   • PersonAvatar    — resolves a person's CURRENT avatar (newest kind='avatar'
//                        media linked to them) then renders <Avatar>. Used in
//                        lists where only the person id is on hand.
//   • PersonAvatarField — the editor control: current avatar + an "upload"
//                        affordance (multipart -> POST /api/actor/media
//                        kind=avatar, person_id). Newest wins on the server.
import { useEffect, useState } from 'preact/hooks';
import { MediaImage } from './MediaEl';
import { listMedia, uploadMedia, ApiError } from '../lib/api';
import './imageUpload.css';
import './Avatar.css';

/** First letters of the first + last name words (max two), uppercased. Empty
 * names degrade to a single dot rather than a blank circle. */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '·';
  if (words.length === 1) return words[0]!.charAt(0).toUpperCase();
  return (words[0]!.charAt(0) + words[words.length - 1]!.charAt(0)).toUpperCase();
}

/** A deterministic hue (0-359) from the name, so a given person always gets the
 * same calm tint — no palette token exists for this illustrative accent. */
export function tintHue(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i += 1) h = (h * 31 + name.charCodeAt(i)) % 360;
  return h;
}

export type AvatarSize = 'sm' | 'md' | 'lg';

export interface AvatarProps {
  tenantId: string;
  /** The avatar media id, or null/undefined for the initials placeholder. */
  mediaId?: string | null;
  name: string;
  size?: AvatarSize;
}

/** The presentational avatar circle — an uploaded image when present, else the
 * initials placeholder. Always a stable circle so lists never jump. */
export function Avatar({ tenantId, mediaId, name, size = 'md' }: AvatarProps) {
  const base = `avatar avatar--${size}`;
  if (mediaId) {
    return (
      <div class={`${base} avatar--img`}>
        <MediaImage tenantId={tenantId} id={mediaId} alt={name} className="avatar__img" />
      </div>
    );
  }
  const hue = tintHue(name);
  return (
    <div
      class={`${base} avatar--initials`}
      role="img"
      aria-label={name}
      style={`--avatar-h:${hue}`}
      data-testid="avatar-initials"
    >
      <span class="avatar__initials" aria-hidden="true">
        {initialsOf(name)}
      </span>
    </div>
  );
}

export interface PersonAvatarProps {
  tenantId: string;
  personId: string;
  name: string;
  size?: AvatarSize;
  /** Bump to force a re-fetch of the current avatar (after an upload). */
  refreshKey?: number;
}

/** Resolves a person's CURRENT avatar (newest kind='avatar' media for them) and
 * renders it, degrading to the initials placeholder while loading, on failure,
 * or when the person has no avatar yet. */
export function PersonAvatar({ tenantId, personId, name, size, refreshKey }: PersonAvatarProps) {
  const [mediaId, setMediaId] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setMediaId(null);
    listMedia(tenantId, 'avatar', { personId })
      .then((rows) => {
        // Newest first (server reverses created_at ASC) -> index 0 is current.
        if (alive) setMediaId(rows[0]?.id ?? null);
      })
      .catch(() => {
        // No avatar / unreachable -> the initials placeholder is the fallback.
      });
    return () => {
      alive = false;
    };
  }, [tenantId, personId, refreshKey]);

  return <Avatar tenantId={tenantId} mediaId={mediaId} name={name} size={size} />;
}

export interface PersonAvatarFieldProps {
  tenantId: string;
  personId: string;
  name: string;
  /** Called after a successful upload (so the surface behind can refresh). */
  onUploaded?: () => void;
}

/** The editor control: the person's current avatar + an "upload / change"
 * affordance. Uploads via the media pipeline (kind=avatar, person link) then
 * re-fetches so the new portrait shows immediately. */
export function PersonAvatarField({ tenantId, personId, name, onUploaded }: PersonAvatarFieldProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  async function onPick(e: Event) {
    const inputEl = e.target as HTMLInputElement;
    const file = inputEl.files?.[0];
    inputEl.value = '';
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await uploadMedia(tenantId, 'avatar', file, { personId });
      setRefreshKey((k) => k + 1);
      onUploaded?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not upload the avatar.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="avatar-field">
      <PersonAvatar tenantId={tenantId} personId={personId} name={name} size="lg" refreshKey={refreshKey} />
      <div class="avatar-field__control">
        <label class="img-upload__btn">
          <input
            type="file"
            accept="image/*"
            class="img-upload__input"
            onChange={(e) => void onPick(e)}
            disabled={busy}
            data-testid="avatar-upload-input"
          />
          {busy ? 'Uploading…' : 'Upload avatar'}
        </label>
        {error && <div class="img-upload__error">{error}</div>}
      </div>
    </div>
  );
}
